<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import type { BibleVerse, BibleReadingSelection } from '../../types/bible'
import type { VerseSelectionSubmit } from '../../types/verseSelection'
import type { MapPlace } from '../../types/map'
import { useVerseSelection, clampMenuPosition } from '../../composables/useVerseSelection'
import { parseStrongsMarkup } from '../../utils/strongsMarkup'
import { highlightGeoNames } from '../../utils/geoHighlight'
import VerseContextMenu from './VerseContextMenu.vue'

interface Props {
  verses: BibleVerse[]
  loading?: boolean
  error?: string | null
  selection: BibleReadingSelection
  strongsEnabled?: boolean
  geoEnabled?: boolean
  geoPlaces?: MapPlace[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  error: null,
  strongsEnabled: false,
  geoEnabled: false,
  geoPlaces: undefined,
})

const emit = defineEmits<{
  (e: 'submit', payload: VerseSelectionSubmit): void
  (e: 'strongs-click', number: string, testament: 'OT' | 'NT', word: string): void
  (e: 'morph-click', code: string): void
  (e: 'geo-click', placeId: string): void
}>()

const frameContainer = ref<HTMLElement | null>(null)
const frameContent = ref<HTMLElement | null>(null)
const contextMenuRef = ref<HTMLElement | null>(null)
const availableHeight = ref(0)
const minHeightForTwoVerses = 120

const verse = useVerseSelection()

const allVerseNumbers = computed(() => props.verses.map(v => v.verse))

const selectedVersesSorted = computed(() =>
  Array.from(verse.selectedVerses.value).sort((a, b) => a - b),
)

let lastMouseDownPos = { x: 0, y: 0 }
const DRAG_THRESHOLD = 4

const updateAvailableHeight = () => {
  if (!frameContainer.value) return

  const rect = frameContainer.value.getBoundingClientRect()
  const parentRect = frameContainer.value.parentElement?.getBoundingClientRect()
  if (!parentRect) return

  const toolbarHeight = rect.top - parentRect.top
  const availableSpace = parentRect.height - toolbarHeight
  availableHeight.value = Math.max(availableSpace, minHeightForTwoVerses)
}

const resizeObserver = ref<ResizeObserver | null>(null)

const geoNameList = computed(() => {
  if (!props.geoPlaces) return []
  return props.geoPlaces.flatMap(p =>
    p.chineseNames.map(name => ({ name, placeId: p.id }))
  )
})

const renderVerseText = (text: string): string => {
  if (props.strongsEnabled) return parseStrongsMarkup(text, true)
  if (props.geoEnabled && geoNameList.value.length > 0) {
    return highlightGeoNames(text, geoNameList.value)
  }
  return text
}

const handleStrongsClick = (e: MouseEvent) => {
  const target = (e.target as HTMLElement).closest('.strongs-ref, .morph-code')
  if (!target) return

  // 詞性代碼點擊 - 特殊處理
  if (target.classList.contains('morph-code')) {
    const code = target.getAttribute('data-code')
    if (code) {
      emit('morph-click', code)
    }
    return
  }

  // 強字號點擊
  const strongsNumber = target.getAttribute('data-strongs')
  const testament = target.getAttribute('data-testament') as 'OT' | 'NT' | null
  const word = target.textContent || ''

  if (strongsNumber && testament) {
    emit('strongs-click', strongsNumber, testament, word)
  }
}

const handleGeoClick = (e: MouseEvent) => {
  const target = (e.target as HTMLElement).closest('.geo-highlight')
  if (!target) return
  const placeId = target.getAttribute('data-place-id')
  if (placeId) emit('geo-click', placeId)
}

const handleVerseTextClick = (e: MouseEvent) => {
  if (props.strongsEnabled) handleStrongsClick(e)
  else if (props.geoEnabled) handleGeoClick(e)
}

const handleFrameMouseDown = (e: MouseEvent, verseNumber: number) => {
  // 若點擊了強字號相關元素或地名元素，不啟動拖曳選取
  const target = (e.target as HTMLElement).closest('.strongs-ref, .morph-code, .geo-highlight')
  if (target && (props.strongsEnabled || props.geoEnabled)) {
    return
  }

  lastMouseDownPos = { x: e.clientX, y: e.clientY }
  verse.startDrag(verseNumber, e.ctrlKey)

  if (frameContent.value) {
    frameContent.value.style.userSelect = 'none'
  }

  // 添加臨時的 mouseup 監聽器，在捕獲階段確保狀態被重置
  const tempMouseUpHandler = (e: Event) => {
    const mouseEvent = e as MouseEvent
    if (frameContent.value) {
      frameContent.value.style.userSelect = ''
    }

    // 對於 Ctrl+Click，菜單的 @mouseup.stop 會攔截事件，所以需要在這裡呼叫 endDrag
    // 檢查是否在菜單上（通過檢查 target）
    const target = mouseEvent.target as HTMLElement
    const isInMenu = target.closest('.verse-context-menu') !== null

    if (isInMenu && mouseEvent.ctrlKey && verse.isDragging.value) {
      verse.endDrag()
    }

    document.removeEventListener('mouseup', tempMouseUpHandler, true)
  }

  document.addEventListener('mouseup', tempMouseUpHandler, true)
}

const handleFrameMouseMove = (e: MouseEvent) => {
  if (!verse.isDragging.value) return

  const dx = e.clientX - lastMouseDownPos.x
  const dy = e.clientY - lastMouseDownPos.y

  if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
    return
  }

  const target = (e.target as HTMLElement).closest('[data-verse]')
  if (!target) return

  const verseAttr = target.getAttribute('data-verse')
  if (!verseAttr) return

  const targetVerse = parseInt(verseAttr, 10)
  verse.extendDrag(targetVerse, allVerseNumbers.value)

  if (frameContent.value) {
    verse.startAutoScroll(frameContent.value, e.clientY)
  }
}

const handleFrameMouseUp = (e: MouseEvent) => {
  if (frameContent.value) {
    frameContent.value.style.userSelect = ''
  }

  if (!verse.isDragging.value) {
    return
  }

  const dx = e.clientX - lastMouseDownPos.x
  const dy = e.clientY - lastMouseDownPos.y

  // 距離小於閾值才視為單擊
  if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
    const target = (e.target as HTMLElement).closest('[data-verse]')
    if (target) {
      const verseAttr = target.getAttribute('data-verse')
      if (verseAttr) {
        const verseNumber = parseInt(verseAttr, 10)

        // Ctrl+Click：不連續選取
        if (e.ctrlKey) {
          verse.toggleVerse(verseNumber)
          verse.endDrag()
          if (verse.selectedVerses.value.size > 0) {
            showContextMenu(e)
          }
          return
        }

        // 單純點擊：如果表單已打開，關閉它；否則打開表單
        if (verse.contextMenu.visible) {
          verse.clearSelection()
          verse.endDrag()
          return
        }
        verse.selectSingle(verseNumber)
      }
    }
  }

  verse.endDrag()

  if (verse.selectedVerses.value.size > 0) {
    showContextMenu(e)
  }
}

const handleContextMenu = (e: MouseEvent) => {
  const target = (e.target as HTMLElement).closest('[data-verse]')
  if (target && verse.selectedVerses.value.size > 0) {
    e.preventDefault()
    showContextMenu(e)
  }
}

const showContextMenu = (e: MouseEvent) => {
  if (!frameContainer.value) return

  const rect = frameContainer.value.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top

  const menuWidth = 120
  const menuHeight = 80

  const clamped = clampMenuPosition(
    x,
    y,
    rect.width,
    rect.height,
    menuWidth,
    menuHeight,
  )

  verse.showContextMenu(clamped.x, clamped.y)
}

const handleContextMenuAction = (action: 'bookmark' | 'note') => {
  const payload: VerseSelectionSubmit = {
    action,
    verses: selectedVersesSorted.value,
    selection: props.selection,
  }
  emit('submit', payload)
  verse.clearSelection()
}

const handleDocumentMouseDown = (e: MouseEvent) => {
  const target = e.target as HTMLElement

  const isInFrame = frameContainer.value?.contains(target)
  const isInMenu = !!target.closest('.verse-context-menu')

  if (!isInFrame && !isInMenu) {
    verse.clearSelection()
  }
}

onMounted(() => {
  updateAvailableHeight()

  if (frameContainer.value?.parentElement) {
    resizeObserver.value = new ResizeObserver(() => {
      updateAvailableHeight()
    })
    resizeObserver.value.observe(frameContainer.value.parentElement)
  }

  window.addEventListener('resize', updateAvailableHeight)

  document.addEventListener('mousedown', handleDocumentMouseDown)

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Control') {
      verse.setCtrlPressed(true)
    }
  }

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Control') {
      verse.setCtrlPressed(false)
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('keyup', handleKeyUp)

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('keyup', handleKeyUp)
  })
})

onUnmounted(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
  window.removeEventListener('resize', updateAvailableHeight)
  document.removeEventListener('mousedown', handleDocumentMouseDown)
  verse.clearSelection()
})

const frameStyle = computed(() => ({
  maxHeight: `${availableHeight.value}px`,
}))
</script>

<template>
  <div ref="frameContainer" class="bible-reading-frame">
    <div class="frame-header">
      <div class="header-no">No.</div>
      <div class="header-text">經文</div>
    </div>

    <div
      ref="frameContent"
      class="frame-content"
      :style="frameStyle"
      @mousemove="handleFrameMouseMove"
      @mouseup="handleFrameMouseUp"
      @contextmenu="handleContextMenu"
    >
      <!-- 載入狀態 -->
      <div v-if="loading" class="state-message">
        載入經文中...
      </div>

      <!-- 錯誤狀態 -->
      <div v-else-if="error" class="state-message error">
        {{ error }}
      </div>

      <!-- 空狀態 -->
      <div v-else-if="verses.length === 0" class="state-message">
        請選擇聖經章節
      </div>

      <!-- 經節列表 -->
      <div v-else class="verses-table">
        <div
          v-for="verseItem in verses"
          :key="verseItem.verse"
          class="verse-row"
          :class="{ 'verse-selected': verse.isSelected(verseItem.verse) }"
          :data-verse="verseItem.verse"
          @mousedown="handleFrameMouseDown($event, verseItem.verse)"
        >
          <div class="verse-no">{{ verseItem.verse }}</div>
          <div
            class="verse-text"
            :class="{ 'strongs-enabled': props.strongsEnabled, 'geo-enabled': props.geoEnabled }"
            @click="handleVerseTextClick"
            v-html="renderVerseText(verseItem.text)"
          />
        </div>
      </div>

      <VerseContextMenu
        ref="contextMenuRef"
        :visible="verse.contextMenu.visible"
        :x="verse.contextMenu.x"
        :y="verse.contextMenu.y"
        @action="handleContextMenuAction"
      />
    </div>
  </div>
</template>

<style scoped>
.bible-reading-frame {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #e0e0e0;
  background-color: #ffffff;
  user-select: text;
  min-height: 0;
  flex: 1;
}

.frame-header {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 12px;
  gap: 12px;
  flex-shrink: 0;
}

.header-no {
  width: 60px;
  text-align: center;
  font-size: calc(var(--font-size-base) * 0.8);
  font-weight: 600;
  color: #333;
}

.header-text {
  flex: 1;
  text-align: center;
  font-size: calc(var(--font-size-base) * 0.8);
  font-weight: 600;
  color: #333;
}

.frame-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.state-message {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: #999;
  font-size: var(--font-size-base);
}

.state-message.error {
  color: #dc2626;
}

.verses-table {
  display: flex;
  flex-direction: column;
}

.verse-row {
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.1s;
  cursor: text;
  user-select: none;
}

.verse-row:last-child {
  border-bottom: none;
}

.verse-row.verse-selected {
  background-color: #fff3cd;
}

.verse-no {
  width: 60px;
  text-align: center;
  flex-shrink: 0;
  font-size: calc(var(--font-size-base) * 0.8);
  color: #666;
  padding-top: 2px;
}

.verse-text {
  flex: 1;
  font-size: var(--font-size-base);
  line-height: 1.6;
  color: #333;
  word-break: break-word;
  white-space: normal;
  user-select: text;
}

/* HTML 標籤樣式 */
.verse-text h1,
.verse-text h2,
.verse-text h3,
.verse-text h4,
.verse-text h5,
.verse-text h6 {
  font-size: 1em;
  font-weight: 600;
  margin: 0.2em 0;
  color: #333;
}

.verse-text u {
  text-decoration: underline;
}

.verse-text i,
.verse-text em {
  font-style: italic;
}

.verse-text b,
.verse-text strong {
  font-weight: 600;
}

.verse-text br {
  display: inline;
}

.verse-text p {
  margin: 0.2em 0;
}

/* 捲軸樣式 */
.frame-content::-webkit-scrollbar {
  width: 6px;
}

.frame-content::-webkit-scrollbar-track {
  background-color: #f5f5f5;
}

.frame-content::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.frame-content::-webkit-scrollbar-thumb:hover {
  background-color: #999;
}

/* 強字號編號標記樣式 */
.verse-text.strongs-enabled :deep(.strongs-ref) {
  display: inline-block;
  font-size: calc(var(--font-size-base) * 0.75);
  color: #0066cc;
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px dotted #0066cc;
  margin: 0 2px;
  padding: 0 2px;
  transition: all 0.2s;
}

.verse-text.strongs-enabled :deep(.strongs-ref:hover) {
  background-color: rgba(0, 102, 204, 0.1);
  border-radius: 2px;
}

/* 詞性代碼標記樣式 - 獨立可點擊 */
.verse-text.strongs-enabled :deep(.morph-code) {
  display: inline-block;
  font-size: calc(var(--font-size-base) * 0.7);
  vertical-align: super;
  color: #0066cc;
  cursor: pointer;
  border-bottom: 1px dotted #0066cc;
  margin: 0 1px;
  padding: 0 1px;
  transition: all 0.2s;
  font-weight: 500;
}

.verse-text.strongs-enabled :deep(.morph-code:hover) {
  background-color: rgba(0, 102, 204, 0.1);
  border-radius: 1px;
}

/* 地名反白樣式 */
.verse-text.geo-enabled :deep(.geo-highlight) {
  background-color: #fef08a;
  border-radius: 2px;
  cursor: pointer;
  padding: 0 1px;
  transition: background-color 0.2s;
}

.verse-text.geo-enabled :deep(.geo-highlight:hover) {
  background-color: #fde047;
}
</style>
